import type { ReverseAnalyticsData } from 'services/clickstream';
import type { PayHubCashierContractsPendingWithdrawalResponse } from 'store/models';
import { percentage, subtract, sum, truncateNumber } from 'utils/math/math';

import { FULL_REVERSE_PERCENT } from '../utils';

const PERCENT_OPTIONS = [25, 50, 75, FULL_REVERSE_PERCENT];

const DEFAULT_REVERSE_PRECISION = 2;

interface ReverseAmounts {
  returnAmount: number;
  remainAmount: number;
}

export const calculateReverseAmounts = (
  amount: number,
  percent: number,
  precision = DEFAULT_REVERSE_PRECISION,
): ReverseAmounts => {
  if (!Number.isFinite(amount) || !Number.isFinite(percent) || percent <= 0) {
    return {
      returnAmount: 0,
      remainAmount: amount,
    };
  }

  const returnAmount = truncateNumber(percentage(amount, percent), precision);
  const remainAmount = subtract(amount, returnAmount).toNumber();

  return {
    returnAmount,
    remainAmount,
  };
};

export const isPercentAvailable = (withdrawal: PayHubCashierContractsPendingWithdrawalResponse, percent: number) =>
  withdrawal.available_options.includes(percent);

export const getAvailablePercents = (withdrawals: PayHubCashierContractsPendingWithdrawalResponse[]) =>
  PERCENT_OPTIONS.filter(
    (percent) =>
      percent === FULL_REVERSE_PERCENT || withdrawals.some((withdrawal) => isPercentAvailable(withdrawal, percent)),
  );

export const getPreselectedPercent = (
  preselectedOption: number,
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[],
): number | null => {
  if (!preselectedOption) {
    return null;
  }

  return getAvailablePercents(withdrawals).find((percent) => percent >= preselectedOption) ?? null;
};

export const getAvailableOrderIds = (withdrawals: PayHubCashierContractsPendingWithdrawalResponse[], percent: number) =>
  withdrawals.filter((withdrawal) => isPercentAvailable(withdrawal, percent)).map(({ order_id }) => order_id);

export const calculateTotalReturn = (
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[],
  checkedIds: number[],
  percent: number,
  precision = DEFAULT_REVERSE_PRECISION,
) =>
  sum(
    withdrawals
      .filter(({ order_id }) => checkedIds.includes(order_id))
      .map(({ amount }) => calculateReverseAmounts(amount, percent, precision).returnAmount),
  ).toNumber();

export const getReverseAnalyticsData = (
  withdrawals: PayHubCashierContractsPendingWithdrawalResponse[],
  checkedIds: number[],
  percent: number | null,
  precision = DEFAULT_REVERSE_PRECISION,
): ReverseAnalyticsData => {
  if (percent === null) {
    return {
      option: null,
      return_amount: null,
      remain_amount: null,
      transaction_count: checkedIds.length,
    };
  }

  const amounts = withdrawals
    .filter(({ order_id }) => checkedIds.includes(order_id))
    .map(({ amount }) => calculateReverseAmounts(amount, percent, precision));

  return {
    option: percent,
    return_amount: sum(amounts.map(({ returnAmount }) => returnAmount)).toFixed(),
    remain_amount: sum(amounts.map(({ remainAmount }) => remainAmount)).toFixed(),
    transaction_count: amounts.length,
  };
};

export const NO_AMOUNT_RETURNED_ERROR = 'no_amount_returned';

export const getReverseErrorType = (error: unknown): string => {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: { error_code?: number } };

  return String(data?.error_code ?? status ?? 'unknown');
};
